import React, {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-toastify";

import css from './SemiFinishedUpdateModal.module.css';
import {
    arrayFormattedProducts,
    arrayFormattedSemiFinished,
    semiFinishedActions,
    semiFinishedProductsActions,
    semiFinishedSemiFinishedActions
} from "../../../../store/slices/semiFinished-slice";
import {semiFinishedService} from "../../../../services/semiFinished-services";
import {SemiFinishedLeftBlockModal} from "../SemiFinishedLeftBlockModal/SemiFinishedLeftBlockModal";
import {SemiFinishedRightBlockModal} from "../SemiFinishedRightBlockModal/SemiFinishedRightBlockModal";
import {SemiFinishedAddProductModal} from "../SemiFinishedAddProductModal/SemiFinishedAddProductModal";
import {SemiFinishedAddSemiFinishedModal} from "../SemiFinishedAddSemiFinishedModal/SemiFinishedAddSemiFinishedModal";


const SemiFinishedUpdateModal = ({setIsOpenUpdate, semiFinished}) => {
    const {
        id, name, fats, products, weight, weightNetto, carbs, proteins, price, cost,
        categoryId, img, description, recipe, energy, includedSemiFinisheds
    } = semiFinished;

    const [selectedCategory, setSelectedCategory] = useState(categoryId || "");
    const [semiFinishedProductAddOpen, setSemiFinishedProductAddOpen] = useState(false);
    const [semiFinishedSemiFinishedAddOpen, setSemiFinishedSemiFinishedAddOpen] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    
    // Track if this is the initial load to prevent overwriting DB values
    const isInitialLoad = useRef(true);

    const dispatch = useDispatch();
    const {t} = useTranslation();

    const {
        semiFinishedProductsWeight,
        semiFinishedProductsWeightNetto,
        semiFinishedProductsCost,
        semiFinishedProductEnergy,
        semiFinishedProductsFats,
        semiFinishedProductsProteins,
        semiFinishedProductsCarbohydrates
    } = useSelector(state => state.semiFinishedProducts);

    const {
        semiFinishedSemiFinishedWeight,
        semiFinishedSemiFinishedWeightNetto,
        semiFinishedSemiFinishedCost,
        semiFinishedSemiFinishedEnergy,
        semiFinishedSemiFinishedFats,
        semiFinishedSemiFinishedProteins,
        semiFinishedSemiFinishedCarbohydrates
    } = useSelector(state => state.semiFinishedSemiFinished);

    useEffect(() => {
        dispatch(semiFinishedProductsActions.updateArray(products));
        dispatch(semiFinishedSemiFinishedActions.updateArray(includedSemiFinisheds));
        dispatch(semiFinishedProductsActions.changeTrigger());
        
        // Mark initial load as complete after a short delay to allow redux updates
        setTimeout(() => {
            isInitialLoad.current = false;
        }, 100);
    }, [dispatch]);

    const methods = useForm({
        defaultValues: {
            weight: Number(weight),
            weightNet: Number(weightNetto),
            Cost: Number(cost),
            Energy: Number(energy),
            Proteins: Number(proteins),
            Fats: Number(fats),
            Carbohydrates: Number(carbs),
            price: price,
            nameEn: name.en,
            nameRu: name.ru,
            nameKa: name.ge,
            descriptionEn: description.en,
            descriptionRu: description.ru,
            descriptionKa: description.ge,
            recipeEn: recipe.en,
            recipeRu: recipe.ru,
            recipeGe: recipe.ge,
            categoryId: categoryId || "",
            nameAr: name.ar,
            descriptionAr: description.ar,
            recipeAr: recipe.ar,
            energyOverridden: semiFinished.energyOverridden ?? false,
            proteinsOverridden: semiFinished.proteinsOverridden ?? false,
            fatsOverridden: semiFinished.fatsOverridden ?? false,
            carbohydratesOverridden: semiFinished.carbohydratesOverridden ?? false,
            costOverridden: semiFinished.costOverridden ?? false,
        },
        mode: "onChange",
    });

    const {setValue, watch} = methods;

    const energyOverridden = watch("energyOverridden");
    const proteinsOverridden = watch("proteinsOverridden");
    const fatsOverridden = watch("fatsOverridden");
    const carbohydratesOverridden = watch("carbohydratesOverridden");
    const costOverridden = watch("costOverridden");

    // Only recalculate values when ingredients change, not on initial load
    useEffect(() => {
        if (!isInitialLoad.current) {
            setValue("weight", semiFinishedProductsWeight + semiFinishedSemiFinishedWeight);
            setValue("weightNet", semiFinishedProductsWeightNetto + semiFinishedSemiFinishedWeightNetto);
            setValue("Cost", semiFinishedProductsCost + semiFinishedSemiFinishedCost);
            setValue("Fats", semiFinishedProductsFats + semiFinishedSemiFinishedFats);
            setValue("Proteins", semiFinishedProductsProteins + semiFinishedSemiFinishedProteins);
            setValue("Carbohydrates", semiFinishedProductsCarbohydrates + semiFinishedSemiFinishedCarbohydrates);
            setValue("Energy", semiFinishedProductEnergy + semiFinishedSemiFinishedEnergy);
        }
    }, [
        semiFinishedProductsWeight, semiFinishedSemiFinishedWeight,
        semiFinishedProductsWeightNetto, semiFinishedSemiFinishedWeightNetto,
        semiFinishedProductsCost, semiFinishedSemiFinishedCost,
        semiFinishedProductsFats, semiFinishedSemiFinishedFats,
        semiFinishedProductsProteins, semiFinishedSemiFinishedProteins,
        semiFinishedProductsCarbohydrates, semiFinishedSemiFinishedCarbohydrates,
        semiFinishedProductEnergy, semiFinishedSemiFinishedEnergy,
        setValue
    ]);

    const formattedProducts = useSelector(arrayFormattedProducts);
    const formattedSemiFinished = useSelector(arrayFormattedSemiFinished);

    const handleCategoryChange = (e) => {
        const value = e.target.value;
        setSelectedCategory(value);
        setValue("categoryId", value);
    };

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();

            if (uploadedImage) {
                formData.append("file", uploadedImage);
            }

            formData.append("name", JSON.stringify({
                en: data.nameEn,
                ru: data.nameRu,
                ge: data.nameKa,
                ar: data.nameAr
            }));
            formData.append("description", JSON.stringify({
                en: data.descriptionEn,
                ru: data.descriptionRu,
                ge: data.descriptionKa,
                ar: data.descriptionAr
            }));
            formData.append("recipe", JSON.stringify({
                en: data.recipeEn,
                ru: data.recipeRu,
                ge: data.recipeGe,
                ar: data.recipeAr
            }));

            formData.append("img", img || '');
            formData.append("cost", Number(data.Cost) || 0);
            formData.append("price", parseFloat(data.price) || 0);
            formData.append("weight", Number(data.weight) || 0);
            formData.append("weightNetto", Number(data.weightNet) || 0);
            formData.append("categoryId", data.categoryId || '');
            formData.append("products", JSON.stringify(formattedProducts));
            formData.append("semiFinisheds", JSON.stringify(formattedSemiFinished));

            if (data.energyOverridden) {
                formData.append("energy", data.Energy);
            }
            if (data.proteinsOverridden) {
                formData.append("proteins", data.Proteins);
            }
            if (data.fatsOverridden) {
                formData.append("fats", data.Fats);
            }
            if (data.carbohydratesOverridden) {
                formData.append("carbohydrates", data.Carbohydrates);
            }

            formData.append("energyOverridden", data.energyOverridden ? "true" : "false");
            formData.append("proteinsOverridden", data.proteinsOverridden ? "true" : "false");
            formData.append("fatsOverridden", data.fatsOverridden ? "true" : "false");
            formData.append("carbohydratesOverridden", data.carbohydratesOverridden ? "true" : "false");
            formData.append("costOverridden", data.costOverridden ? "true" : "false");

            await semiFinishedService.updateSemiFinishById(id, formData);
            dispatch(semiFinishedActions.changeTrigger());
            toast.success(t('Semi-finished product updated!'));
            setIsOpenUpdate(false);
        } catch (error) {
            console.error(error);
            toast.error(t('modal-toast-creating-error'));
        }
    };

    return (
        <>
            <section className={css.overlay} onClick={() => setIsOpenUpdate(false)}>
                <div className={css.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={css.header}>
                        <div className={css.title}>{t("modal-update-semi-finished")}</div>
                        <div className={css.closeButton} onClick={() => setIsOpenUpdate(false)}>×</div>
                    </div>

                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onSubmit)} className={css.form}>
                            <div className={css.infoBlock}>
                                <div className={css.leftBlock}>
                                    <SemiFinishedLeftBlockModal
                                        setSemiFinishedProductAddOpen={setSemiFinishedProductAddOpen}
                                        setSemiFinishedSemiFinishedAddOpen={setSemiFinishedSemiFinishedAddOpen}
                                        setUploadedImage={setUploadedImage}
                                        initialImage={img}
                                    />
                                </div>

                                <div className={css.rightBlock}>
                                    <SemiFinishedRightBlockModal
                                        selectedCategory={selectedCategory}
                                        setSelectedCategory={setSelectedCategory}
                                        handleCategoryChange={handleCategoryChange}
                                    />
                                </div>
                            </div>

                            <div className={css.buttonsBlock}>
                                <button type="button" className={css.cancelButton}
                                        onClick={() => setIsOpenUpdate(false)}>{t('modal-cancel')}</button>
                                <button type="submit" className={css.submitButton}>OK</button>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </section>

            {semiFinishedProductAddOpen &&
                <SemiFinishedAddProductModal setSemiFinishedProductAddOpen={setSemiFinishedProductAddOpen}/>}
            {semiFinishedSemiFinishedAddOpen &&
                <SemiFinishedAddSemiFinishedModal setSemiFinishedSemiFinishedAddOpen={setSemiFinishedSemiFinishedAddOpen}/>}
        </>
    );
};

export {SemiFinishedUpdateModal};

